import { Col, Input, InputNumber, Row, Select } from "antd";
import {
  IInteractiveModLocation,
  interactiveLocationActions,
  interactiveLocationApps,
  interactiveLocationTravelModes,
} from "@blings/blings-player";
import { ModsPanelCompProps } from "../../ModsPanel";
import { AddInteractiveHOC } from "./InteractiveFormHOC";
import MainCTAButton from "../../../MainCTA/MainCTAButton";
import { LocationSourceEditor } from "./LocationSourceEditor";
import {
  getLocationFieldVisibility,
  isLocationTravelModeSupported,
} from "../../locationConnector";
import { useInteractiveCtaNameAutofill } from "./useInteractiveCtaNameAutofill";
import { useEffect } from "react";

const APP_OPTIONS = [
  { value: interactiveLocationApps.system, label: "Device default" },
  { value: interactiveLocationApps.ask, label: "Ask viewer" },
  { value: interactiveLocationApps.googleMaps, label: "Google Maps" },
  { value: interactiveLocationApps.appleMaps, label: "Apple Maps" },
  { value: interactiveLocationApps.waze, label: "Waze" },
];

const TRAVEL_MODE_OPTIONS = [
  { value: undefined, label: "Provider default" },
  { value: interactiveLocationTravelModes.driving, label: "Driving" },
  { value: interactiveLocationTravelModes.walking, label: "Walking" },
  { value: interactiveLocationTravelModes.cycling, label: "Cycling" },
  { value: interactiveLocationTravelModes.transit, label: "Transit" },
];

const renderLocationForm = (
  props: ModsPanelCompProps<IInteractiveModLocation>
) => {
  const { change, index, onChange } = props;
  const fieldVisibility = getLocationFieldVisibility(change.action);

  const handleActionChange = (action: interactiveLocationActions) => {
    onChange(
      index,
      ["action", "zoom"],
      action === interactiveLocationActions.show
        ? [action, change.zoom ?? 16]
        : [action, undefined]
    );
  };

  return (
    <>
      <Row style={{ width: "100%" }}>
        <Col style={{ width: "100%" }}>
          <span className="location-field-label">
            Name this button (Required for analytics)
          </span>
          <Input
            size="large"
            id="connector-cta-name"
            value={change.ctaName}
            placeholder="Name this button"
            onChange={(event) => onChange(index, "ctaName", event.target.value)}
            allowClear
          />
        </Col>
      </Row>

      <label>
        <span className="location-field-label">Action</span>
        <Select
          size="large"
          style={{ width: "100%" }}
          value={change.action}
          onChange={handleActionChange}
          options={[
            {
              value: interactiveLocationActions.show,
              label: "Show location",
            },
            {
              value: interactiveLocationActions.navigate,
              label: "Get directions",
            },
          ]}
        />
      </label>

      <label>
        <span className="location-field-label">Open with</span>
        <Select
          size="large"
          style={{ width: "100%" }}
          value={change.app}
          onChange={(app) => onChange(index, "app", app)}
          options={APP_OPTIONS.map((option) => ({
            ...option,
            disabled:
              option.value === interactiveLocationApps.system &&
              change.action === interactiveLocationActions.navigate,
            title:
              option.value === interactiveLocationApps.system &&
              change.action === interactiveLocationActions.navigate
                ? "Device default cannot start turn-by-turn navigation."
                : undefined,
          }))}
        />
      </label>

      <LocationSourceEditor change={change} index={index} onChange={onChange} />

      {fieldVisibility.zoom && (
        <label>
          <span className="location-field-label">Zoom</span>
          <InputNumber
            size="large"
            style={{ width: "100%" }}
            min={2}
            max={21}
            precision={0}
            value={change.zoom}
            onChange={(zoom) => onChange(index, "zoom", zoom ?? undefined)}
          />
        </label>
      )}

      {fieldVisibility.travelMode && (
        <label>
          <span className="location-field-label">Travel mode</span>
          <Select
            size="large"
            style={{ width: "100%" }}
            value={change.travelMode}
            placeholder="Provider default"
            onChange={(travelMode) => onChange(index, "travelMode", travelMode)}
            options={TRAVEL_MODE_OPTIONS.map((option) => ({
              ...option,
              disabled: !isLocationTravelModeSupported(
                change.app,
                option.value
              ),
              title:
                !isLocationTravelModeSupported(change.app, option.value) &&
                change.app === interactiveLocationApps.appleMaps
                  ? "Apple Maps does not support cycling in this connector."
                  : !isLocationTravelModeSupported(change.app, option.value)
                  ? "Waze supports driving only in this connector."
                  : undefined,
            }))}
            allowClear
          />
        </label>
      )}

      <MainCTAButton change={change} onChange={onChange} index={index} />
    </>
  );
};

export const AddInteractiveLocationForm = (
  props: ModsPanelCompProps<IInteractiveModLocation>
) => {
  const { change, index, onChange } = props;
  useInteractiveCtaNameAutofill(props);
  useEffect(() => {
    const keys: Array<keyof IInteractiveModLocation> = [];
    const values: any[] = [];
    if (!change.action) {
      keys.push("action");
      values.push(interactiveLocationActions.show);
    }
    if (!change.app) {
      keys.push("app");
      values.push(interactiveLocationApps.system);
    }
    if (keys.length) onChange(index, keys, values);
  }, [change.action, change.app, index, onChange]);

  return (
    <AddInteractiveHOC<IInteractiveModLocation>
      {...props}
      renderSpecificInteractive={renderLocationForm}
    />
  );
};
